import { PayloadAction } from "@reduxjs/toolkit";
import { ITags } from "app/containers/Features/components/CommonTagSelection/types";
import { Board } from "app/containers/Features/containers/Boards/types";

import { initialState } from "app/redux/initialState";
import {
  ArchiveType,
  AuthorizedAccount,
  BoardTemplateEnum,
  BoardType,
  IBoard,
  IBoardFixedTemp,
  IBoardMenu,
  IBoardSetting,
  IProjectSettings,
  ITeamSettings,
  IUserData,
  IWorkSpaceDetails,
  IWorkspaceTeams,
  userOnboardingStage,
} from "app/types";
import { createSlice } from "utils/@reduxjs/toolkit";
import { updateBoardOrderRequest } from "./saga";

const byCreatedAtDesc = (a: ITags, b: ITags) =>
  Date.parse(b.createdAt || "") - Date.parse(a.createdAt || "");

const useClinicSlice = createSlice({
  name: "appHomeState",
  initialState,
  reducers: {
    toggleProfileDrawer(
      state,
      action: PayloadAction<{ actions: boolean; id?: string }>,
    ) {
      state.drawerOpen = action.payload;
    },
    toggleSearchDrawer(state) {
      state.globalSearchModal = !state.globalSearchModal;
    },
    toggleNotificationDrawer(state, action: PayloadAction<boolean>) {
      state.notificationDrawerOpen = action.payload;
    },
    doGetMyProfile: (state) => {},

    setIsCollapseSidebarMenu: (state) => {
      state.isCollapseSidebarMenu = !state.isCollapseSidebarMenu;
    },
    setUserFormData: (state, action: PayloadAction<IUserData>) => {
      state.userFormData = action.payload;
    },
    updateUserFormData: (state, action: PayloadAction<IUserData>) => {
      state.userFormData = action.payload;
    },
    setSelectedTeam: (state, action: PayloadAction<IWorkspaceTeams | null>) => {
      state.selectedTeam = action.payload;
    },

    setUserWorkspaceData: (state, action: PayloadAction<IWorkSpaceDetails>) => {
      state.workSpaceDetails = action.payload;

      const selectedTeamId = state.userFormData?.selectedTeamId ?? null;
      const teams = action.payload?.teams ?? [];
      let selectedTeam: IWorkspaceTeams | null = null;

      if (teams.length > 0) {
        if (selectedTeamId) {
          selectedTeam =
            teams.find((t) => t._id === selectedTeamId) ?? teams[0];
        } else {
          selectedTeam = teams[0];
        }
      } else {
        selectedTeam = null;
      }

      state.selectedTeam = selectedTeam;
    },

    removeTeamById: (state, action: PayloadAction<string>) => {
      const teamId = action.payload;
      // teams remove
      state.workSpaceDetails.teams = state.workSpaceDetails.teams.filter(
        (team) => team._id !== teamId,
      );
      // teamIds remove
      state.workSpaceDetails.teamIds = state.workSpaceDetails.teamIds.filter(
        (id) => id !== teamId,
      );
    },
    doGetUserWorkspace: (
      state,
      action: PayloadAction<{ callback: () => void }>,
    ) => {},
    setIsBoardCreate: (state, action: PayloadAction<boolean>) => {
      state.isBoardCreate = action.payload;
    },
    setTeamSetting: (state, action: PayloadAction<ITeamSettings[]>) => {
      if (!action.payload) return;

      state.workspaceTeamsSetting = action.payload;
    },
    updateBoardsLocally: (state, action: PayloadAction<any>) => {
      state.boardMenuData.push({
        _id: action.payload.boardViewHistoryId,
        name: action.payload.name,
        viewType: action.payload.viewType,
        Type: action.payload.Type,
        boardId: action.payload._id,
        order: action.payload.order,
        favorite: 0,
      });
    },
    fetchTeamsSettings: (
      state,
      action: PayloadAction<{ data: string[] }>,
    ) => {},
    setIsBoardLoading: (state, action: PayloadAction<boolean>) => {
      state.isBoardLoading = action.payload;
    },
    // board create API
    doCreateFixTempBoard: (
      state,
      action: PayloadAction<{
        data: IBoardFixedTemp;
        callback: (data: Board) => void;
      }>,
    ) => {},

    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setAuthorizedAccounts: (
      state,
      action: PayloadAction<AuthorizedAccount[]>,
    ) => {
      state.authorizedAccounts = action.payload;
    },

    setUserOnboardingProgress: (
      state,
      action: PayloadAction<userOnboardingStage>,
    ) => {
      state.userOnboardingProgress = action.payload;
    },
    setUserToken: (state, action: PayloadAction<string>) => {
      state.access_token = action.payload;
    },
    onLogout: (state) => {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.access_token = null;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("projectId");
      state.userFormData = initialState.userFormData;
    },

    setStatusForSelectedTicketType: (
      state,
      action: PayloadAction<{ ticketTypeId: string }>,
    ) => {
      const { ticketTypeId } = action.payload;
      const ticketType = state.projectSettingDetails.ticketTypes.find(
        (type) => type._id === ticketTypeId,
      );

      if (!ticketType) {
        state.workspaceTeamsSetting[0].ticketStatus = [];
        return;
      }

      const validStatuses = state.projectSettingDetails.ticketStatuses.filter(
        (status: { ticketTypes: string[] }) =>
          status.ticketTypes.includes(ticketTypeId),
      );
      state.workspaceTeamsSetting[0].ticketStatus = validStatuses;

      console.log("Valid Statuses: ", validStatuses);
    },

    setTicketLocally: (
      state,
      action: PayloadAction<{
        ticket: any;
        groupId: string;
      }>,
    ) => {},

    createTagLocally: (state, action: PayloadAction<ITags>) => {
      const list = state.projectSettingDetails.ticketTags ?? [];

      const idx = list.findIndex((t) => t._id === action.payload._id);
      if (idx !== -1) {
        list[idx] = action.payload;
      } else {
        list.push(action.payload);
      }

      list.sort(byCreatedAtDesc);

      state.projectSettingDetails.ticketTags = list;
    },

    removeTagsLocally: (state, action: PayloadAction<string>) => {
      const id = action.payload;

      if (!state.projectSettingDetails?.ticketTags) return;

      const list = state.projectSettingDetails.ticketTags;
      const idx = list.findIndex((tag) => tag._id === id);

      if (idx !== -1) {
        list.splice(idx, 1); // Immer-safe
      }
    },

    updateTagsLocallyById: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ITags> }>,
    ) => {
      const { id, changes } = action.payload;

      if (!state.projectSettingDetails?.ticketTags) return;

      const tag = state.projectSettingDetails.ticketTags.find(
        (t) => t._id === id,
      );
      if (tag) {
        Object.assign(tag, changes);
      }
    },

    updateBoardOrderState: (
      state,
      action: PayloadAction<{ selectedId: string; clickedId: string }>,
    ) => {
      const { selectedId, clickedId } = action.payload;
      const boards = state.boardMenuData;
      if (!boards) return;
      console.log(JSON.stringify(boards));

      const selectedIndex = boards.findIndex(
        (card) => card.boardId === selectedId,
      );
      if (!selectedId || boards[selectedIndex].Type === BoardType.Backlog) {
        const clickedIndex = boards.findIndex(
          (card) => card.boardId === clickedId,
        );
        boards[clickedIndex].order = 1;
        boards.forEach((element) => {
          if (element.Type !== BoardType.Backlog) element.order += 1;
        });
        state.boardMenuData = boards;
        state.boards = boards.filter((board) => board.order || board.boardId);
      } else {
        const firstIndex = boards.findIndex(
          (card) => card.boardId === selectedId,
        );
        const secondIndex = boards.findIndex(
          (card) => card.boardId === clickedId,
        );

        if (firstIndex === -1 || secondIndex === -1) return;

        // Swap order
        const temp = boards[firstIndex].order;
        boards[firstIndex].order = boards[secondIndex].order;
        boards[secondIndex].order = temp;
        state.boardMenuData = boards;

        state.boards = boards.filter((board) => board.order || board.boardId);
      }
    },
    updateBoardOrderFromHome: (
      state,
      action: PayloadAction<{
        board: Board;
      }>,
    ) => {
      const { board } = action.payload;
      const allBoards = state.boardMenuData;

      const existingIdx = allBoards.findIndex(
        (card) => card.boardId === board._id,
      );

      if (existingIdx !== -1) {
        state.boardMenuData = allBoards.map((b) => {
          if (b.boardId === board._id) {
            return { ...b, order: 1 };
          }
          if (b.order === 0) {
            return b;
          }
          return { ...b, order: b.order + 1 };
        });
      } else {
        const newBoardMenuItem: IBoardMenu = {
          ...board,
          Type: board.type,
          boardId: board._id,
          order: 1,
        };

        const updated = allBoards.map((b) => {
          if (b.order === 0) return b;
          return { ...b, order: b.order + 1 };
        });

        updated.splice(1, 0, newBoardMenuItem);
        state.boardMenuData = updated;
      }
    },
    updateNewBoardOrderState: (
      state,
      action: PayloadAction<{
        board: Board;
        visibleBoard: number;
      }>,
    ) => {
      const { board, visibleBoard } = action.payload;

      const allBoards = state.boardMenuData;
      if (!allBoards) return;
      const newBoardMenuItem: IBoardMenu = {
        ...board,
        Type: board.type,
        boardId: board._id,
        order: visibleBoard,
      };

      const updated = allBoards.map((b) => {
        if (b.order < visibleBoard) return b;
        return { ...b, order: b.order + 1 };
      });

      updated.splice(1, 0, newBoardMenuItem);
      state.boardMenuData = updated;
      updateBoardOrderRequest();
    },
    doUpdateBoardOrder: (state) => {},

    updateBoardGroup: (
      state,
      action: PayloadAction<{
        boardId: string;
        groupBy: string;
        groups: IBoardSetting[];
        groupJson?: string | null;
      }>,
    ) => {
      const { boardId, groupBy, groups } = action.payload;
      const teamSetting = state.workspaceTeamsSetting;

      // if (!teamSetting?.projects?.[0]?.boards) return;

      // const boards = teamSetting.projects[0].boards;

      // boards.forEach((board) => {
      //   if (board._id === boardId) {
      //     board.settings.groupBy = groupBy as GroupByTypeEnum;
      //     board.settings.groups = groups;
      //   }
      // });
    },
    doGetBoardsMenu: (state, action: PayloadAction<{ id: string }>) => {
      state.isBoardMenuLoading = true;
    },

    setBoardMenuData: (state, action: PayloadAction<IBoardMenu[]>) => {
      state.boardMenuData = action.payload;
    },

    setIsBoardMenuLoading: (state, action: PayloadAction<boolean>) => {
      state.isBoardMenuLoading = action.payload;
    },
    setIsArchiveLoading: (state, action: PayloadAction<boolean>) => {
      state.isArchiveLoading = action.payload;
    },
    setArchiveBoardMenu: (state, action: PayloadAction<string>) => {
      const boardId = action.payload;
      const filterData = state.boardMenuData.filter(
        (board) => board.boardId !== boardId,
      );
      state.boardMenuData = filterData;
    },
    doArchive: (
      state,
      action: PayloadAction<{
        projectId: string;
        dataType: ArchiveType;
        dataId: string[];
        callback: () => void;
      }>,
    ) => {},
    updateBoardMenuName: (
      state,
      action: PayloadAction<{ id: string; name: string }>,
    ) => {
      const boardId = action.payload.id;
      const updatedName = action.payload.name;
      const targetBoard = state.boardMenuData.find(
        (board) => board.boardId === boardId,
      );

      if (targetBoard) {
        targetBoard.name = updatedName;
      }
    },
    toggleBoardMenuFavorite: (state, action: PayloadAction<{ id: string }>) => {
      const targetBoard = state.boardMenuData.find(
        (board) => board.boardId === action.payload.id,
      );
      if (targetBoard) {
        targetBoard.favorite = targetBoard.favorite === 1 ? 0 : 1;
      }
    },

    toggleCreateTeamModal: (state, action: PayloadAction<boolean>) => {
      state.createTeamModal = action.payload;
    },
    fetchSelectedTeam: (state, action: PayloadAction<string>) => {},
    setSelectedTeamLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedTeamLoading = action.payload;
    },
    localToggleHideProject: (
      state,
      action: PayloadAction<{ projectId: string }>,
    ) => {
      const project = state.selectedTeam?.projects.find(
        (project) => project?._id === action.payload.projectId,
      );
      if (project) {
        project.isHiddenInSidebar = !project.isHiddenInSidebar;
      }
    },
    localArchiveProjectFromWorkspace: (
      state,
      action: PayloadAction<{ projectId: string }>,
    ) => {
      const projectId = action.payload.projectId;
      // Remove from selectedTeam projects
      if (state.selectedTeam) {
        state.selectedTeam.projects = state.selectedTeam.projects.filter(
          (project) => project._id !== projectId,
        );
      }
      // Remove from workSpaceDetails teams projects
      state.workSpaceDetails.teams = state.workSpaceDetails.teams.map(
        (team) => ({
          ...team,
          projects: team.projects.filter(
            (project) => project._id !== projectId,
          ),
        }),
      );
    },
    toggleAddProjectModal: (state, action: PayloadAction<boolean>) => {
      state.createProjectModal = action.payload;
    },

    // fetch project setting basic details ( ticket types , status )
    fetchProjectSettings: (state, action: PayloadAction<string>) => {},
    setProjectSettingsDetails: (
      state,
      action: PayloadAction<IProjectSettings>,
    ) => {
      state.projectSettingDetails = action.payload;
    },
    addSelectedTeamToWorkspace: (
      state,
      action: PayloadAction<{
        obj: { teamId: string; projectId: string };
        // callback: () => void;
      }>,
    ) => {},

    // TimeSheet --
    toggleTimeSheet: (state) => {
      state.isTimeSheetEnabled = !state.isTimeSheetEnabled;
    },
    // --

    // Timer --
    start: (state, action: PayloadAction<{ startTime: number }>) => {
      state.timeTracker.startTime = action.payload.startTime;
      state.timeTracker.isRunning = true;
    },

    stop: (state) => {
      if (state.timeTracker.startTime) {
        const now = Date.now();
        state.timeTracker.elapsed += Math.floor(
          (now - state.timeTracker.startTime) / 1000,
        );
      }
      state.timeTracker.startTime = null;
      state.timeTracker.isRunning = false;
    },

    reset: (state) => {
      state.timeTracker.startTime = null;
      state.timeTracker.elapsed = 0;
      state.timeTracker.isRunning = false;
    },

    setElapsed: (state, action: PayloadAction<number>) => {
      state.timeTracker.elapsed = action.payload;
    },

    restore: (
      state,
      action: PayloadAction<{
        startTime: number | null;
        elapsed: number;
        isRunning: boolean;
      }>,
    ) => {
      state.timeTracker.startTime = action.payload.startTime;
      state.timeTracker.elapsed = action.payload.elapsed;
      state.timeTracker.isRunning = action.payload.isRunning;
    },

    // --
  },
});

export const { reducer, actions, name: sliceKey } = useClinicSlice;

// i just update due to my better understating
